import { io, Socket } from 'socket.io-client';
import {
  ORDER_EVENTS,
  KOT_EVENTS,
  TABLE_EVENTS,
  NOTIFICATION_EVENTS,
  SUBSCRIPTION_EVENTS
} from '../constants/socketEvents';

class SocketService {
  private socket: Socket | null = null;
  private token: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private listeners: Map<string, Set<Function>> = new Map();

  // Deduplication: track recently emitted events to prevent duplicate processing
  // Key format: "eventName:dataId" or "eventName:timestamp" for events without IDs
  private recentEvents: Map<string, number> = new Map();
  private readonly DEDUP_WINDOW_MS = 3000; // 3 seconds deduplication window

  initialize(token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Clean up any existing connection first
      if (this.socket) {
        if (this.socket.connected) {
          console.log('Socket already connected, reusing connection');
          resolve();
          return;
        }
        // Disconnect and clean up stale socket
        console.log('Cleaning up existing socket');
        this.socket.removeAllListeners();
        this.socket.disconnect();
        this.socket = null;
      }

      this.token = token;
      
      // Get Socket.IO URL - use port 9092 for Socket.IO server
      const socketUrl = import.meta.env.VITE_SOCKET_URL || 'http://localhost:9092';
      
      console.log('Initializing Socket.IO connection to:', socketUrl);
      console.log('Using token:', token ? `${token.substring(0, 20)}...` : 'NO TOKEN');

      try {
        // Create socket with simple configuration first
        this.socket = io(socketUrl, {
          path: '/socket.io/',
          transports: ['websocket', 'polling'],
          query: {
            token: token
          },
          reconnection: true,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          reconnectionAttempts: this.maxReconnectAttempts,
          // Allow slower cold starts (cloud/proxy) before timing out
          timeout: 45000
        });

        this.socket.on('connect', () => {
          console.log('Socket.IO connected successfully');
          this.reconnectAttempts = 0;
          resolve();
        });

        this.socket.on('connect_error', (error: any) => {
          console.error('Socket.IO connection error:', {
            message: error.message || error,
            type: error.type,
            data: error.data,
            fullError: error
          });
          
          // Check if it's an auth error
          if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
            console.error('Authentication failed - token may be invalid or expired');
            reject(new Error('Authentication failed - please login again'));
            return;
          }
          
          this.reconnectAttempts++;
          
          // Only reject after all retry attempts
          if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('Max reconnection attempts reached');
            reject(new Error('Failed to connect to Socket.IO server after ' + this.maxReconnectAttempts + ' attempts'));
          }
          // Otherwise, let it retry automatically
        });

        this.socket.on('disconnect', (reason) => {
          console.log('Socket.IO disconnected:', reason);
          
          if (reason === 'io server disconnect') {
            // Server disconnected, attempt to reconnect
            this.socket?.connect();
          }
        });

        // Set up global event listeners
        this.setupGlobalListeners();

        // Remove custom manual timeout to avoid racing with built-in reconnection logic
      } catch (error) {
        console.error('Failed to create Socket.IO instance:', error);
        reject(error);
      }
    });
  }

  private setupGlobalListeners() {
    if (!this.socket) return;

    // ============================================================================
    // STANDARDIZED KOT EVENTS
    // ============================================================================
    this.socket.on(KOT_EVENTS.NEW, (data) => {
      console.log('[SocketService] KOT_NEW received:', data);
      this.emitToListeners(KOT_EVENTS.NEW, data);
    });

    this.socket.on(KOT_EVENTS.UPDATED, (data) => {
      console.log('[SocketService] KOT_UPDATED received:', data);
      this.emitToListeners(KOT_EVENTS.UPDATED, data);
    });

    this.socket.on(KOT_EVENTS.STATUS_UPDATE, (data) => {
      console.log('[SocketService] KOT_STATUS_UPDATE received:', data);
      this.emitToListeners(KOT_EVENTS.STATUS_UPDATE, data);
    });

    // ============================================================================
    // STANDARDIZED ORDER EVENTS
    // ============================================================================
    this.socket.on(ORDER_EVENTS.NEW, (data) => {
      console.log('[SocketService] ORDER_NEW received:', data);
      this.emitToListeners(ORDER_EVENTS.NEW, data);
    });

    this.socket.on(ORDER_EVENTS.UPDATED, (data) => {
      console.log('[SocketService] ORDER_UPDATED received:', data);
      this.emitToListeners(ORDER_EVENTS.UPDATED, data);
    });

    this.socket.on(ORDER_EVENTS.STATUS_UPDATE, (data) => {
      console.log('[SocketService] ORDER_STATUS_UPDATE received:', data);
      this.emitToListeners(ORDER_EVENTS.STATUS_UPDATE, data);
    });

    // ============================================================================
    // STANDARDIZED TABLE EVENTS
    // ============================================================================
    this.socket.on(TABLE_EVENTS.STATUS_CHANGED, (data) => {
      console.log('[SocketService] TABLE_STATUS_CHANGED received:', data);
      this.emitToListeners(TABLE_EVENTS.STATUS_CHANGED, data);
    });

    // ============================================================================
    // NOTIFICATION WRAPPER EVENT
    // Normalizes wrapped events to their standardized names
    // ============================================================================
    this.socket.on(NOTIFICATION_EVENTS.GENERAL, (data) => {
      console.log('[SocketService] NOTIFICATION received:', data);
      this.emitToListeners(NOTIFICATION_EVENTS.GENERAL, data);

      try {
        const event = (data?.event || data?.type || '').toString();
        const normalized = this.normalizeKotPayload(data);

        // Normalize KOT creation events
        if ([KOT_EVENTS.NEW, 'kotCreated', 'KOT_CREATED'].includes(event)) {
          console.log('[SocketService] Normalized notification → KOT_NEW', normalized);
          this.emitToListeners(KOT_EVENTS.NEW, normalized);
        }

        // Normalize KOT update events
        if ([KOT_EVENTS.UPDATED, KOT_EVENTS.STATUS_UPDATE, 'kotStatusUpdate'].includes(event)) {
          console.log('[SocketService] Normalized notification → KOT_UPDATED', normalized);
          this.emitToListeners(KOT_EVENTS.UPDATED, normalized);
        }

        // Normalize ORDER events from notification wrapper
        if ([ORDER_EVENTS.NEW, 'NEW_ORDER'].includes(event)) {
          console.log('[SocketService] Normalized notification → ORDER_NEW', data?.data || data);
          this.emitToListeners(ORDER_EVENTS.NEW, data?.data || data);
        }

        if ([ORDER_EVENTS.UPDATED, ORDER_EVENTS.STATUS_UPDATE, 'ORDER_UPDATE'].includes(event)) {
          console.log('[SocketService] Normalized notification → ORDER_STATUS_UPDATE', data?.data || data);
          this.emitToListeners(ORDER_EVENTS.STATUS_UPDATE, data?.data || data);
        }

        // Normalize TABLE events from notification wrapper
        if ([TABLE_EVENTS.STATUS_CHANGED, 'TABLE_UPDATED'].includes(event)) {
          console.log('[SocketService] Normalized notification → TABLE_STATUS_CHANGED', data?.data || data);
          this.emitToListeners(TABLE_EVENTS.STATUS_CHANGED, data?.data || data);
        }
      } catch (e) {
        console.warn('[SocketService] Notification normalization failed:', e);
      }
    });
  }

  // Generate a unique key for deduplication based on event and data
  private generateDedupKey(event: string, data: any): string {
    // Extract identifying information from the data
    const d = data?.data || data || {};
    const status = d.status || '';

    // For KOT events, prioritize kotId/kotNumber since multiple KOTs can exist per order
    // Using orderId would incorrectly deduplicate different KOTs for the same order
    if (event.startsWith('KOT_')) {
      const kotId = d.kotId || d.kotNumber || d.id || '';
      if (kotId) {
        return `${event}:${kotId}:${status}`;
      }
    }

    // For ORDER and other events, use orderId
    const id = d.orderId || d.order_id || d.id || '';
    if (id) {
      return `${event}:${id}:${status}`;
    }

    // Fallback: use stringified data (limited to first 100 chars)
    return `${event}:${JSON.stringify(d).substring(0, 100)}`;
  }

  // Check if event was recently emitted (within dedup window)
  private isDuplicateEvent(dedupKey: string): boolean {
    const now = Date.now();
    const lastEmitted = this.recentEvents.get(dedupKey);

    if (lastEmitted && (now - lastEmitted) < this.DEDUP_WINDOW_MS) {
      return true;
    }

    // Record this event
    this.recentEvents.set(dedupKey, now);

    // Cleanup old entries (keep map size reasonable)
    if (this.recentEvents.size > 100) {
      const cutoff = now - this.DEDUP_WINDOW_MS;
      for (const [key, timestamp] of this.recentEvents.entries()) {
        if (timestamp < cutoff) {
          this.recentEvents.delete(key);
        }
      }
    }

    return false;
  }

  private emitToListeners(event: string, data: any) {
    // Apply deduplication for events that commonly arrive from multiple sources
    const eventsToDedup = [ORDER_EVENTS.NEW, ORDER_EVENTS.UPDATED, ORDER_EVENTS.STATUS_UPDATE,
                          KOT_EVENTS.NEW, KOT_EVENTS.UPDATED, KOT_EVENTS.STATUS_UPDATE,
                          TABLE_EVENTS.STATUS_CHANGED];

    if (eventsToDedup.includes(event)) {
      const dedupKey = this.generateDedupKey(event, data);
      if (this.isDuplicateEvent(dedupKey)) {
        console.log(`[SocketService] SKIPPING duplicate event '${event}' (key: ${dedupKey})`);
        return;
      }
    }

    const eventListeners = this.listeners.get(event);
    console.log(`[SocketService] emitToListeners called for '${event}', listeners count: ${eventListeners?.size || 0}`);
    if (eventListeners) {
      eventListeners.forEach(listener => {
        try {
          listener(data);
        } catch (e) {
          console.error(`[SocketService] Error calling listener for '${event}':`, e);
        }
      });
    }
  }

  // Extract a consistent KOT shape from various backend payloads
  private normalizeKotPayload(raw: any) {
    const n = raw?.data || raw || {};
    return {
      kotNumber: n.kotNumber || n.kot_no || n.kotId || n.id,
      kotId: n.kotId || n.id,
      orderId: n.orderId || n.order_id,
      kitchenId: n.kitchenId || n.kitchen_id || raw?.kitchenId,
      vendorId: n.vendorId || n.vendor_id || raw?.vendorId,
      tableNumber: n.tableNumber || n.table || n.tableName,
      status: n.status || n.kotStatus,
      printed: n.printed,
      ...n,
    };
  }

  on(event: string, callback: Function) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)?.add(callback);
    console.log(`[SocketService] Registered listener for '${event}', total listeners: ${this.listeners.get(event)?.size}`);

    // Note: We don't register directly with socket here because:
    // 1. Global handlers in setupGlobalListeners() already listen for events
    // 2. Those handlers call emitToListeners() which dispatches to our internal listeners
    // 3. Registering here would cause duplicate callbacks
  }

  off(event: string, callback: Function) {
    this.listeners.get(event)?.delete(callback);
    // No need to call socket.off() since we don't register directly in on()
  }

  emit(eventName: string, data: any) {
    if (this.socket?.connected) {
      this.socket.emit(eventName, data);
    } else {
      console.warn('Socket not connected, cannot emit:', eventName);
    }
  }

  // ============================================================================
  // SUBSCRIPTION METHODS (using standardized event names)
  // ============================================================================

  subscribeToOrder(orderId: number) {
    console.log(`[SocketService] Subscribing to order ${orderId}`);
    this.emit(SUBSCRIPTION_EVENTS.SUBSCRIBE_ORDER, { orderId });
  }

  unsubscribeFromOrder(orderId: number) {
    console.log(`[SocketService] Unsubscribing from order ${orderId}`);
    this.emit(SUBSCRIPTION_EVENTS.UNSUBSCRIBE_ORDER, { orderId });
  }

  subscribeToKitchen(kitchenId: number) {
    console.log(`[SocketService] Subscribing to kitchen ${kitchenId}`);
    this.emit(SUBSCRIPTION_EVENTS.SUBSCRIBE_KITCHEN, { kitchenId });
  }

  unsubscribeFromKitchen(kitchenId: number) {
    console.log(`[SocketService] Unsubscribing from kitchen ${kitchenId}`);
    this.emit(SUBSCRIPTION_EVENTS.UNSUBSCRIBE_KITCHEN, { kitchenId });
  }

  subscribeToVendor(vendorId: number) {
    console.log(`[SocketService] Subscribing to vendor ${vendorId}`);
    this.emit(SUBSCRIPTION_EVENTS.SUBSCRIBE_VENDOR, { vendorId });
  }

  unsubscribeFromVendor(vendorId: number) {
    console.log(`[SocketService] Unsubscribing from vendor ${vendorId}`);
    this.emit(SUBSCRIPTION_EVENTS.UNSUBSCRIBE_VENDOR, { vendorId });
  }

  subscribeToTable(tableNumber: string, vendorId: number) {
    console.log(`[SocketService] Subscribing to table ${tableNumber} for vendor ${vendorId}`);
    this.emit(SUBSCRIPTION_EVENTS.SUBSCRIBE_TABLE, { tableNumber, vendorId });
  }

  unsubscribeFromTable(tableNumber: string, vendorId: number) {
    console.log(`[SocketService] Unsubscribing from table ${tableNumber}`);
    this.emit(SUBSCRIPTION_EVENTS.UNSUBSCRIBE_TABLE, { tableNumber, vendorId });
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  disconnect() {
    if (this.socket) {
      console.log('Disconnecting Socket.IO');
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.listeners.clear();
    }
  }

  reconnect() {
    if (this.socket && !this.socket.connected) {
      console.log('Attempting to reconnect Socket.IO');
      this.socket.connect();
    }
  }
}

export default new SocketService();